import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import VerifiedQueryService from '../../../services/VerifiedQueryService';
import HelpIcon from '../../../components/help/HelpIcon';

const darkField = {
  '& .MuiInputBase-root': { backgroundColor: 'var(--wb-bg-surface)', color: 'var(--wb-text-title)' },
  '& .MuiInputBase-input': { color: 'var(--wb-text-title)' }
};

const monoField = {
  ...darkField,
  '& .MuiInputBase-input': { color: 'var(--wb-text-title)', fontFamily: 'ui-monospace, Menlo, Consolas, monospace', fontSize: '0.85rem' }
};

const EMPTY_FORM = { question: '', sqlQuery: '', description: '', semanticModelId: '', status: 'ACTIVE' };

/**
 * Alta/edición de una verified query (pregunta -> SQL verificado). Si
 * `item` viene informado, edita esa fila; si no, crea una nueva.
 *
 * `semanticModelId` vacío = "Global" (visible para cualquier agente de la
 * cuenta, ver tools/verified_query_tool.py) -- no es lo mismo que "sin
 * elegir todavía", así que el Select siempre tiene una opción explícita
 * para ese caso en vez de dejarlo simplemente sin seleccionar.
 */
const VerifiedQueryFormDialog = ({ open, item, models, onClose, onSaved }) => {
  const isEditing = Boolean(item?.query_id);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setError('');
    setForm(
      item
        ? {
            question: item.question || '',
            sqlQuery: item.sql_query || '',
            description: item.description || '',
            semanticModelId: item.semantic_model_id || '',
            status: item.status || 'ACTIVE'
          }
        : EMPTY_FORM
    );
  }, [open, item]);

  const handleChange = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    try {
      if (isEditing) {
        await VerifiedQueryService.update(item.query_id, form);
      } else {
        await VerifiedQueryService.create(form);
      }
      onSaved();
    } catch (err) {
      setError(err?.message || 'No se pudo guardar la verified query.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {isEditing ? 'Editar verified query' : 'Nueva verified query'}
        <IconButton onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <Box component="form" onSubmit={handleSubmit}>
        <DialogContent dividers sx={{ display: 'grid', gap: 2.5 }}>
          {error && <Alert severity="error">{error}</Alert>}

          <TextField
            label="Pregunta"
            required
            multiline
            minRows={2}
            value={form.question}
            onChange={handleChange('question')}
            helperText="La pregunta en lenguaje natural tal como la haría un usuario."
            sx={darkField}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <HelpIcon module="agentes" topic="consultas-verificadas" anchor="pregunta" />
                </InputAdornment>
              )
            }}
          />

          <TextField
            label="SQL verificado"
            required
            multiline
            minRows={4}
            value={form.sqlQuery}
            onChange={handleChange('sqlQuery')}
            helperText="Solo SELECT/WITH -- se valida en el servidor antes de guardar."
            sx={monoField}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <HelpIcon module="agentes" topic="consultas-verificadas" anchor="sql-verificado" />
                </InputAdornment>
              )
            }}
          />

          <TextField
            label="Descripción (opcional)"
            multiline
            minRows={2}
            value={form.description}
            onChange={handleChange('description')}
            sx={darkField}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <HelpIcon module="agentes" topic="consultas-verificadas" anchor="descripción-opcional" />
                </InputAdornment>
              )
            }}
          />

          <Stack direction="row" alignItems="flex-start" spacing={0.5}>
            <FormControl sx={darkField} fullWidth>
              <InputLabel>Modelo semántico</InputLabel>
              <Select
                label="Modelo semántico"
                value={form.semanticModelId}
                onChange={handleChange('semanticModelId')}
              >
                <MenuItem value="">
                  <em>Global (todos los modelos de la cuenta)</em>
                </MenuItem>
                {models.map((m) => (
                  <MenuItem key={m.semantic_model_id} value={m.semantic_model_id}>
                    {m.name || m.semantic_model_id}
                  </MenuItem>
                ))}
              </Select>
              <Typography variant="caption" sx={{ color: 'var(--wb-muted-70)', mt: 0.5, px: 1.5 }}>
                Limita esta verified query a un modelo -- evita que se sugiera SQL con tablas de otro modelo.
              </Typography>
            </FormControl>
            <HelpIcon module="agentes" topic="consultas-verificadas" anchor="modelo-semántico" sx={{ mt: 0.5 }} />
          </Stack>

          {isEditing && (
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.status === 'ACTIVE'}
                    onChange={(e) => setForm((p) => ({ ...p, status: e.target.checked ? 'ACTIVE' : 'DISABLED' }))}
                  />
                }
                // PENDING_REVIEW/REJECTED (candidatos de la Fase 8, ver
                // "Pendientes de revisión") también pueden editarse acá --
                // activar el switch los aprueba/reactiva igual que el botón
                // dedicado de esa pestaña.
                label={
                  form.status === 'ACTIVE'
                    ? 'Activa'
                    : form.status === 'PENDING_REVIEW'
                      ? 'Pendiente de revisión (activar para aprobar)'
                      : form.status === 'REJECTED'
                        ? 'Rechazada (activar para reactivar)'
                        : 'Deshabilitada'
                }
              />
              <HelpIcon module="agentes" topic="consultas-verificadas" anchor="estado-solo-al-editar" />
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={onClose} disabled={saving} sx={{ textTransform: 'none' }}>
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={saving || !form.question.trim() || !form.sqlQuery.trim()}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {saving ? 'Guardando...' : isEditing ? 'Guardar cambios' : 'Crear'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default VerifiedQueryFormDialog;
